import { useState } from 'react';
import * as XLSX from 'xlsx';
import SpreadsheetPicker from './SpreadsheetPicker';
import { Avatar, Flex, Button } from '@vibe/core';
import { Rotate as RotateIcon, ExternalPage } from '@vibe/icons';
import { mondayWorkbookToTransportFormat, BoardTransportFormat, transportFormatToWorkbook } from '../services/converter';
import monday from '../services/monday';
import { useMe } from '../hooks/use-me';
import { ErrorBox } from '@gorilla/ui/src/components/ErrorBox';
import BetaOverlay from './BetaOverlay';

function ConverterWidget() {
  const [error, setError] = useState<any>(null);
  const [transportFormat, setTransportFormat] = useState<BoardTransportFormat | null>(null);
  //const [showBetaOverlay, setShowBetaOverlay] = useState(true);
  const [showBetaOverlay, setShowBetaOverlay] = useState(() => {
    return window.localStorage.getItem(`eec-onboarding--dismissed`) !== 'yes';
  });
  const { user, error: userError } = useMe();

  if (!user && !userError) {
    return null;
  }

  if (userError) {
    return (
      <div className="SuperConverter SuperConverter--app">
        <ErrorBox error={userError} />
      </div>
    );
  }

  return (
    <div className="SuperConverter SuperConverter--app">
      {showBetaOverlay && (
        <BetaOverlay
          onClose={() => {
            setShowBetaOverlay(false);
            window.localStorage.setItem(`eec-onboarding--dismissed`, 'yes');
          }}
        />
      )}
      {error ? <ErrorBox error={error} /> : null}

      <div className={error ? 'Main Error' : 'Main'}>
        <div className="Wrapper">
          <div className={transportFormat ? 'SpreadsheetPicker' : 'SpreadsheetPicker SpreadsheetPicker--Empty'}>
            <SpreadsheetPicker
              format={!!transportFormat}
              onChange={(workbook) => {
                try {
                  setError(null);
                  setTransportFormat(workbook ? mondayWorkbookToTransportFormat(workbook) : null);
                } catch (err) {
                  setTransportFormat(null);
                  setError(err);
                }
              }}
              onError={(err) => {
                setTransportFormat(null);
                setError(err);
              }}
            />
          </div>

          {transportFormat ? (
            <>
              <div className="DownloadPicker">
                <div className="DownloadPicker-items">
                  <div className={transportFormat ? 'DownloadPicker-item' : 'DownloadPicker-item disabled'}>
                    <div
                      className="DownloadPicker-itemInner"
                      onClick={() => {
                        const workbook = transportFormatToWorkbook(transportFormat);
                        XLSX.writeFile(workbook, `${transportFormat.name}.xlsx`, { compression: true });
                        monday.execute('valueCreatedForUser');
                      }}
                    >
                      <Avatar
                        withoutBorder
                        square
                        size="medium"
                        type="icon"
                        icon={RotateIcon}
                        customBackgroundColor={transportFormat ? '#0073E3' : '#d0d4e4'}
                      />
                      <div>
                        <h4>Convert to XLSX</h4>
                        <label>without formatting</label>
                      </div>
                    </div>
                  </div>
                  <div className={transportFormat ? 'DownloadPicker-item' : 'DownloadPicker-item disabled'}>
                    <div
                      className="DownloadPicker-itemInner"
                      onClick={() => {
                        const workbook = transportFormatToWorkbook(transportFormat);
                        XLSX.writeFile(workbook, `${transportFormat.name}.ods`);
                        monday.execute('valueCreatedForUser');
                      }}
                    >
                      <Avatar
                        withoutBorder
                        square
                        size="medium"
                        type="icon"
                        icon={RotateIcon}
                        customBackgroundColor={transportFormat ? '#0073E3' : '#d0d4e4'}
                      />
                      <div>
                        <h4>Convert to ODS</h4>
                        <label>without formatting</label>
                      </div>
                    </div>
                  </div>
                  <div className={transportFormat ? 'DownloadPicker-item' : 'DownloadPicker-item disabled'}>
                    <div
                      className="DownloadPicker-itemInner"
                      onClick={() => {
                        const workbook = transportFormatToWorkbook(transportFormat);
                        XLSX.writeFile(workbook, `${transportFormat.name}.html`);
                        monday.execute('valueCreatedForUser');
                      }}
                    >
                      <Avatar
                        withoutBorder
                        square
                        size="medium"
                        type="icon"
                        icon={RotateIcon}
                        customBackgroundColor={transportFormat ? '#0073E3' : '#d0d4e4'}
                      />
                      <div>
                        <h4>Convert to HTML</h4>
                        <label>without formatting</label>
                      </div>
                    </div>
                  </div>
                  <div className={transportFormat ? 'DownloadPicker-item' : 'DownloadPicker-item disabled'}>
                    <div
                      className="DownloadPicker-itemInner"
                      onClick={() => {
                        const workbook = transportFormatToWorkbook(transportFormat);
                        XLSX.writeFile(workbook, `${transportFormat.name}.csv`);
                        monday.execute('valueCreatedForUser');
                      }}
                    >
                      <Avatar
                        withoutBorder
                        square
                        size="medium"
                        type="icon"
                        icon={RotateIcon}
                        customBackgroundColor={transportFormat ? '#0073E3' : '#d0d4e4'}
                      />
                      <div>
                        <h4>Convert to CSV</h4>
                        <label>with items only</label>
                      </div>
                    </div>
                  </div>
                  {transportFormat.subitems ? (
                    <div className={transportFormat ? 'DownloadPicker-item' : 'DownloadPicker-item disabled'}>
                      <div
                        className="DownloadPicker-itemInner"
                        onClick={() => {
                          const workbook = transportFormatToWorkbook(transportFormat, false, true);
                          XLSX.writeFile(workbook, `${transportFormat.name} Subitems.csv`);
                          monday.execute('valueCreatedForUser');
                        }}
                      >
                        <Avatar
                          withoutBorder
                          square
                          size="medium"
                          type="icon"
                          icon={RotateIcon}
                          customBackgroundColor={transportFormat ? '#0073E3' : '#d0d4e4'}
                        />
                        <div>
                          <h4>Convert to CSV</h4>
                          <label>with subitems only</label>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </>
          ) : null}
        </div>

        <div className="Teasers">
          <div className="Teaser--item Teaser--itemGreen">
            <Flex direction="column">
              <h2>
                Did you already try <br />
                Spreadsheet Gorilla?
              </h2>
              <p>
                Spreadsheet Gorilla is your all-in-one solution <br />
                for creating and exporting fully customized <br />
                Excel reports, manually or on a schedule.
              </p>
            </Flex>
            <Button
              rightIcon={ExternalPage}
              size="medium"
              kind="primary"
              color="on-primary-color"
              onClick={() => {
                //createSignal('open_spreadsheet_gorilla');
                window.open('https://getgorilla.app/products/spreadsheet-gorilla', '_blank');
              }}
            >
              Learn more
            </Button>
          </div>
          <div className="Teaser--item">
            <Flex direction="column">
              <h2>
                Supported formats in <br />
                Exported Excel Cleaner
              </h2>
              <p>
                The app supports specific Excel formats <br />
                exported from monday.com boards. If you <br />
                encounter issues, please{' '}
                <a href="https://getgorilla.app/contact" target="_blank" style={{ fontSize: '16px', color: '#fff' }}>
                  contact us
                </a>
                .
              </p>
            </Flex>
            <Button
              rightIcon={ExternalPage}
              size="medium"
              kind="primary"
              color="on-primary-color"
              onClick={() => {
                //createSignal('open_spreadsheet_gorilla');
                window.open('https://getgorilla.app/products/exported-excel-cleaner/docs/supported-formats', '_blank');
              }}
            >
              Supported formats
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConverterWidget;
